import { useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './EventMap.css';
import { DEPT_CENTROIDS, FRANCE_CENTER } from '@/data/departementsCentroids';
import { Target, Trophy, Crosshair, Store } from 'lucide-react';
import { Link } from 'react-router-dom';

export const CATEGORY_META = {
  ball_trap_campagne: {
    label: 'Ball-Trap de Campagne',
    color: '#FF6B35',
    icon: Target,
    shortLabel: 'Campagne',
  },
  concours: {
    label: 'Concours / Compétitions',
    color: '#FFC107',
    icon: Trophy,
    shortLabel: 'Concours',
  },
  stand_de_tir: {
    label: 'Stands de tir',
    color: '#3B82F6',
    icon: Crosshair,
    shortLabel: 'Stands',
  },
  armurerie: {
    label: 'Armureries',
    color: '#22C55E',
    icon: Store,
    shortLabel: 'Armureries',
  },
};

// Slight jitter so multiple events in the same département don't overlap.
function jitter(seed) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  // Pseudo-random in [-0.12, +0.12] degrees ≈ ±13 km
  const a = ((h >>> 0) % 1000) / 1000 - 0.5;
  const b = (((h >>> 10) >>> 0) % 1000) / 1000 - 0.5;
  return [a * 0.24, b * 0.24];
}

function symbolFor(category) {
  // Single-letter pictogram inside the pin (kept simple so it renders on all browsers).
  if (category === 'ball_trap_campagne') return '🎯';
  if (category === 'concours') return '🏆';
  if (category === 'stand_de_tir') return '⊕';
  if (category === 'armurerie') return '🛒';
  return '•';
}

function buildIcon(category, featured) {
  return L.divIcon({
    className: '',
    html: `<div class="cp-marker ${category} ${featured ? 'featured' : ''}"><span>${symbolFor(category)}</span></div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -28],
  });
}

function resolveCoords(evt) {
  // Prefer precise lat/lng if both present and non-zero.
  const lat = parseFloat(evt.lat);
  const lng = parseFloat(evt.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)) {
    return [lat, lng];
  }
  const c = DEPT_CENTROIDS[evt.department];
  if (!c) return null;
  const [dlat, dlng] = jitter(evt.id || evt.name || '');
  return [c[0] + dlat, c[1] + dlng];
}

function formatDateRange(start, end) {
  if (!start && !end) return null;
  const opts = { day: '2-digit', month: 'long', year: 'numeric' };
  const fmt = (s) => {
    try { return new Date(s).toLocaleDateString('fr-FR', opts); } catch { return s; }
  };
  if (start && end && start !== end) return `${fmt(start)} → ${fmt(end)}`;
  return fmt(start || end);
}

function FitBounds({ points }) {
  const map = useMap();
  if (points.length === 0) return null;
  if (points.length === 1) {
    map.setView(points[0], 9, { animate: true });
    return null;
  }
  const bounds = L.latLngBounds(points);
  map.fitBounds(bounds, { padding: [40, 40], maxZoom: 11 });
  return null;
}

export default function EventMap({ events, activeCategories, onMarkerClick }) {
  const markers = useMemo(() => {
    const out = [];
    for (const evt of events || []) {
      const cat = evt.category || 'ball_trap_campagne';
      if (activeCategories && activeCategories.size > 0 && !activeCategories.has(cat)) continue;
      const coords = resolveCoords(evt);
      if (!coords) continue;
      const today = new Date().toISOString().slice(0, 10);
      const isFeatured = !!evt.featured && (!evt.featured_until || evt.featured_until >= today);
      out.push({ evt, coords, cat, featured: isFeatured });
    }
    return out;
  }, [events, activeCategories]);

  const points = markers.map((m) => m.coords);

  return (
    <div className="relative w-full h-[480px] sm:h-[560px] rounded-2xl overflow-hidden border border-white/10">
      <MapContainer
        center={FRANCE_CENTER}
        zoom={6}
        scrollWheelZoom
        style={{ width: '100%', height: '100%' }}
        data-testid="event-map"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          className="cp-leaflet-tiles"
        />
        <FitBounds points={points} />
        {markers.map(({ evt, coords, cat, featured }) => {
          const meta = CATEGORY_META[cat] || CATEGORY_META.ball_trap_campagne;
          const dateLine = formatDateRange(evt.date_start, evt.date_end);
          const isPlace = cat === 'stand_de_tir' || cat === 'armurerie';
          return (
            <Marker
              key={evt.id}
              position={coords}
              icon={buildIcon(cat, featured)}
              eventHandlers={onMarkerClick ? { click: () => onMarkerClick(evt) } : undefined}
            >
              <Popup>
                <div className="min-w-[200px]">
                  <div
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-1.5"
                    style={{ background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}55` }}
                  >
                    {meta.shortLabel}
                  </div>
                  <div className="font-bold text-white text-sm leading-tight mb-1">{evt.name}</div>
                  {evt.organizer && (
                    <div className="text-[#FFC107]/80 text-xs mb-1">{evt.organizer}</div>
                  )}
                  <div className="text-[#aaa] text-xs space-y-0.5">
                    {!isPlace && dateLine && <div>📅 {dateLine}</div>}
                    {(evt.city || evt.address) && (
                      <div>📍 {evt.city || evt.address}{evt.department ? ` · ${evt.department}` : ''}</div>
                    )}
                  </div>
                  <Link
                    to={`/ball-trap-campagne?event=${evt.id}`}
                    className="inline-block mt-2.5 text-xs font-semibold text-[#FFC107] hover:underline"
                  >
                    Voir les détails →
                  </Link>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Legend in corner — top-right so it doesn't cover southern markers */}
      <div className="absolute top-3 right-3 z-[400] flex flex-col gap-1.5 max-w-[160px]">
        {Object.entries(CATEGORY_META).map(([k, meta]) => (
          <span
            key={k}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-black/85 backdrop-blur border border-white/10 text-[10px] text-white/90"
          >
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: meta.color }} />
            {meta.shortLabel}
          </span>
        ))}
      </div>

      {markers.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="px-4 py-2 rounded-full bg-black/80 border border-white/10 text-white/70 text-xs">
            Aucun résultat avec ces filtres
          </div>
        </div>
      )}
    </div>
  );
}
